import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { string, object } from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import Autocomplete from "../../../../../components/react-form/Autocomplete";
import ZipTextField from "../../../../../components/react-form/ZipTextField";
import {
  getCities,
  getEntity,
  getState,
  storeEntity,
  updateEntity,
} from "../../../../../services/pwsApi";

const entitySchema = object().shape({
  description: string().required("Informe a descrição"),
  cpfCnpj: string().required("Informe um CPF ou CNPJ"),
  address: string(),
  addressNumber: string(),
  neighborhood: string(),
  postalCode: string(),
  addressState: object().nullable(),
  addressCity: object().nullable(),
  phone: string(),
});

export default function EntityDialog({ onClose, entityCode }) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const companyCode = searchParams.get("company_code");
  const company = companyCode?.slice(0, 3);
  const subsidiary = companyCode?.slice(3, 7);

  const entityQuery = useQuery(
    ["entity", entityCode],
    () => getEntity(company, subsidiary, entityCode),
    {
      enabled: !!entityCode && !!company && !!subsidiary,
      select: response => {
        const entity = response.data?.data;
        if (!entity) return null;
        return {
          id: entity.R_E_C_N_O_,
          code: entity.RA0_ENTIDA,
          description: entity.RA0_DESC,
          cpfCnpj: entity.RA0_CGC,
          address: entity.RA0_ENDERE,
          addressNumber: entity.RA0_NUMEND,
          neighborhood: entity.RA0_BAIRRO,
          city: entity.RA0_CIDADE,
          postalCode: entity.RA0_CEP,
          addressState: entity.RA0_ESTADO
            ? { value: entity.RA0_ESTADO, label: entity.RA0_ESTADO }
            : null,
          addressCity: entity.RA0_CODMUN
            ? { value: entity.RA0_CODMUN, label: entity.RA0_CIDADE }
            : null,
          phone: entity.RA0_FONE,
        };
      },
    },
  );

  const data = entityQuery.data;

  const statesQuery = useQuery(
    ["states", company, subsidiary],
    () => getState(company, subsidiary),
    {
      enabled: !!company && !!subsidiary,
      select: response =>
        response.data.data.map(st => ({
          value: st?.X5_CHAVE,
          label: st?.X5_CHAVE,
        })),
      onError: () => toast.error("Erro ao carregar estados."),
    },
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(entitySchema),
    defaultValues: {
      id: data?.id ?? null,
      code: data?.code ?? "",
      description: data?.description ?? "",
      cpfCnpj: data?.cpfCnpj ?? "",
      address: data?.address ?? "",
      addressNumber: data?.addressNumber ?? "",
      neighborhood: data?.neighborhood ?? "",
      city: data?.city ?? "",
      postalCode: data?.postalCode ?? "",
      addressState: data?.addressState ?? null,
      addressCity: data?.addressCity ?? null,
      phone: data?.phone ?? "",
    },
  });

  const addressState = watch("addressState");

  const citiesQuery = useQuery(
    ["cities", company, subsidiary, addressState?.value],
    () => getCities(company, subsidiary, addressState?.value),
    {
      enabled: !!company && !!subsidiary && !!addressState?.value,
      select: response =>
        response.data.data.map(city => ({
          value: city?.CC2_CODMUN,
          label: city?.CC2_MUN,
        })),
      onError: () => toast.error("Erro ao carregar cidades."),
    },
  );

  useEffect(() => {
    if (data) {
      reset({
        id: data.id ?? null,
        code: data.code ?? "",
        description: data.description ?? "",
        cpfCnpj: data.cpfCnpj ?? "",
        address: data.address ?? "",
        addressNumber: data.addressNumber ?? "",
        neighborhood: data.neighborhood ?? "",
        city: data.city ?? "",
        postalCode: data.postalCode ?? "",
        addressState: data.addressState ?? null,
        addressCity: data.addressCity ?? null,
        phone: data.phone ?? "",
      });
    }
  }, [data, reset]);

  const storeMutation = useMutation(storeEntity, {
    onSuccess: () => {
      toast.success("Entidade criada com sucesso.");
      queryClient.invalidateQueries("entities");
      queryClient.invalidateQueries("entity");
      onClose();
    },
    onError: () => {
      toast.error("Erro ao criar entidade.");
    },
  });

  const updateMutation = useMutation(
    variables => updateEntity(variables.code, variables),
    {
      onSuccess: () => {
        toast.success("Entidade atualizada com sucesso.");
        queryClient.invalidateQueries("entities");
        queryClient.invalidateQueries("entity");
        onClose();
      },
      onError: () => {
        toast.error("Erro ao atualizar entidade.");
      },
    },
  );

  const onSubmit = formData => {
    const {
      id,
      code,
      description,
      cpfCnpj,
      address,
      addressNumber,
      neighborhood,
      city,
      postalCode,
      addressState,
      addressCity,
      phone,
    } = formData;

    const payload = {
      companyCode: company,
      subsidiaryCode: subsidiary,
      code,
      description,
      cpfCnpj: cpfCnpj?.replace(/\D/g, ""),
      address,
      addressNumber,
      neighborhood,
      city: addressCity?.label || city,
      zipCode: postalCode?.replace(/\D/g, ""),
      state: addressState?.value,
      cityCode: addressCity?.value,
      phone: phone?.replace(/\D/g, ""),
    };

    if (id) {
      updateMutation.mutate({ id, ...payload });
    } else {
      storeMutation.mutate(payload);
    }
  };

  return (
    <Dialog maxWidth="md" fullWidth open onClose={onClose}>
      <DialogTitle>
        {!data?.id ? "Adicionar entidade" : "Editar entidade"}
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          <div className="col-md-8">
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="Descrição"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />
          </div>
          <div className="col-md-4">
            <Controller
              name="cpfCnpj"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="CPF/CNPJ"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.cpfCnpj}
                  helperText={errors.cpfCnpj?.message}
                />
              )}
            />
          </div>
          <div className="col-md-3">
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Telefone"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
          </div>
          <div className="col-md-3">
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <ZipTextField
                  {...field}
                  label="CEP"
                  margin="normal"
                  fullWidth
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message}
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Endereço"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <Controller
              name="addressNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.addressNumber}
                  helperText={errors.addressNumber?.message}
                />
              )}
            />
          </div>
          <div className="col-md-3">
            <Controller
              name="neighborhood"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Bairro"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.neighborhood}
                  helperText={errors.neighborhood?.message}
                />
              )}
            />
          </div>
          <div className="col-md-3">
            <Controller
              name="addressState"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Autocomplete
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  options={statesQuery.data || []}
                  loading={statesQuery.isLoading}
                  inputprops={{
                    label: "UF",
                    fullWidth: true,
                    error: !!errors?.addressState,
                    helperText: errors?.addressState?.message,
                    margin: "normal",
                  }}
                />
              )}
            />
          </div>
          <div className="col-md-3">
            <Controller
              name="addressCity"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Autocomplete
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  options={citiesQuery.data || []}
                  loading={citiesQuery.isLoading}
                  disabled={!addressState?.value}
                  inputprops={{
                    label: "Município",
                    fullWidth: true,
                    error: !!errors?.addressCity,
                    helperText: errors?.addressCity?.message,
                    margin: "normal",
                  }}
                />
              )}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
        >
          {!data?.id ? "Adicionar" : "Editar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
